// TZ-684 — i18next initialisation for the site.
//
// We deliberately do NOT use gatsby-plugin-react-i18next: the plugin generates
// language versions for ALL pages, including the ~100 blog articles that are
// out of scope for translation. That would produce /en/blog/* URLs holding
// Polish content — duplicates and a low-quality signal to Google. Instead we
// create language pages explicitly and declare coverage in
// src/config/i18nCoverage.js.
//
// The path logic and the language list live in i18nCoverage.js (CJS), because
// gatsby-config.js needs the very same code when building the sitemap. Here we
// only re-export them with types — a single source of truth.

import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import {
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  PREFIXED_LANGUAGES,
  OG_LOCALE,
  normalizePlPath,
  getLanguageFromPath,
  buildLanguagePath,
  languagesForPath,
  hasLanguage,
} from "../config/i18nCoverage";

import plCommon from "../locales/pl/common.json";
import plHomepage from "../locales/pl/homepage.json";
import plNav from "../locales/pl/nav.json";
import plFooter from "../locales/pl/footer.json";
import plLanding from "../locales/pl/landing.json";
import plContact from "../locales/pl/contact.json";
import plAbout from "../locales/pl/about.json";
import plCityLanding from "../locales/pl/cityLanding.json";

// 🔴 SŁOWNIKI EN I UK CELOWO NIE SĄ TU IMPORTOWANE.
//
// Ten moduł wchodzi w łańcuch wrapRootElement, czyli ląduje we współdzielonym
// app.js ładowanym na KAŻDEJ stronie. Gdy stały tu wszystkie trzy języki,
// app.js ważył 914 KB surowo / 149,4 KB gzip zamiast 118,9 / 37,8 — polski
// pacjent pobierał ukraiński i angielski cennik ośmiu miast, żeby przeczytać
// polską stronę. Przy ~99% ruchu polskojęzycznego to był czysty koszt CWV.
//
// Zamiast tego każda strona /en/ i /uk/ importuje efektem ubocznym
// ./register/en albo ./register/uk (patrz komentarz tam). Webpack widzi, że te
// moduły są osiągalne wyłącznie ze stron danego języka, i pakuje je do
// osobnego chunku ładowanego tylko tam.
//
// Dodając nową stronę językową NIE dopisuj tu importu — dopisz import
// rejestrujący w pliku strony. Gdyby zabrakło, i18next zejdzie do
// fallbackLng ("pl"), więc objaw to polska treść na stronie obcojęzycznej,
// a nie wyjątek: bramka noPolishOnForeignPages złapie to po buildzie.

export type Language = "pl" | "en" | "uk";

export const NAMESPACES = [
  "common",
  "homepage",
  "nav",
  "footer",
  "landing",
  "city",
  "contact",
  "about",
  // Przebudowany landing miejski — teksty wspólne dla wszystkich 12 miast.
  "cityLanding",
  // Bon podarunkowy razem z regulaminem. Jak `city` — NIE w statycznych
  // zasobach poniżej, bo to ~4,5 KB regulaminu potrzebnego jednej stronie.
  // Polski dokłada ./register/plGift, importowany ze strony bonu.
  "gift",
] as const;

const resources = {
  pl: {
    common: plCommon,
    homepage: plHomepage,
    nav: plNav,
    footer: plFooter,
    landing: plLanding,
    // `city` NIE jest tu ładowane — 226 KB cenników ośmiu miast na każdej
    // stronie bloga to czysty koszt. Dokłada je ./register/plCity, importowany
    // ze stron miejskich.
    contact: plContact,
    about: plAbout,
    cityLanding: plCityLanding,
  },
  // en / uk dokładane w czasie wykonania przez ./registerEn i ./registerUk.
};

if (!i18n.isInitialized) {
  i18n.use(initReactI18next).init({
    resources,
    lng: DEFAULT_LANGUAGE,
    fallbackLng: DEFAULT_LANGUAGE,
    // Synchronous initialisation. By default i18next finishes init
    // asynchronously, which leaves `i18n.language` still empty on the FIRST
    // render — and when generating static pages it is exactly that render which
    // ends up in the HTML. Symptom: the `data-lng` attribute on the widget
    // button vanished from the built page, because React skips attributes whose
    // value is undefined.
    initImmediate: false,
    defaultNS: "common",
    ns: [...NAMESPACES],
    interpolation: {
      // React escapes on its own — double escaping breaks Polish characters
      // and entities in the marketing copy.
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  });
}

export {
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  PREFIXED_LANGUAGES,
  OG_LOCALE,
  normalizePlPath,
  getLanguageFromPath,
  buildLanguagePath,
  languagesForPath,
  hasLanguage,
};

export default i18n;
